import { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, Typography, CircularProgress } from '@mui/material'

import { gqlMutate } from '@hapana/shared/web/services/requests.service'
import {
  generateStripeOnboardingLinkMutation,
  generateStripeLoginLinkMutation,
} from '_services/graphql/payment-gateway.mutations'

import { getBannerStyles } from './payment-gateway-banner.styles'

const SEVERITY_COLORS = {
  error: { bg: 'error.dark', text: 'common.white' },
  warning: { bg: 'warning.dark', text: 'common.white' },
  info: { bg: 'info.main', text: 'common.white' },
}

const PaymentGatewayBanner = ({ siteId, onboardingStatus, hasEventuallyDueRequirements }) => {
  const { t } = useTranslation('core')
  const [isLoading, setIsLoading] = useState(false)

  const handleAction = useCallback(
    async (actionType) => {
      if (!siteId || isLoading) {
        return
      }
      setIsLoading(true)
      try {
        if (actionType === 'dashboard') {
          const result = await gqlMutate({
            mutation: generateStripeLoginLinkMutation,
            variables: { siteId },
            options: { isV1: false },
          })
          const url = result?.generateStripeLoginLink
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer')
          }
        } else {
          const result = await gqlMutate({
            mutation: generateStripeOnboardingLinkMutation,
            variables: { siteId },
            options: { isV1: false },
          })
          const url = result?.generateStripeOnboardingLink?.url
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer')
          }
        }
      } catch (error) {
        // Silent fail — link generation errors are non-critical
      } finally {
        setIsLoading(false)
      }
    },
    [siteId, isLoading]
  )

  const bannerInfo = useMemo(() => {
    if (!onboardingStatus || onboardingStatus === 'not_started') {
      return null
    }

    if (onboardingStatus === 'onboarded') {
      if (hasEventuallyDueRequirements) {
        return {
          severity: 'warning',
          message: t('paymentGatewayBanner.onboarded.message', {
            defaultValue: 'Upcoming Stripe requirements need attention before the deadline.',
          }),
          actionLabel: t('paymentGatewayBanner.onboarded.action', {
            defaultValue: 'View Details',
          }),
          actionType: 'dashboard',
        }
      }
      return null
    }

    const BANNER_CONFIG = [
      {
        status: 'suspended',
        severity: 'error',
        message: t('paymentGatewayBanner.suspended.message', {
          defaultValue: 'Payment processing is suspended. Action needed to resume payments.',
        }),
        actionLabel: t('paymentGatewayBanner.suspended.action', {
          defaultValue: 'Resolve Now',
        }),
        actionType: 'onboarding',
      },
      {
        status: 'rejected',
        severity: 'error',
        message: t('paymentGatewayBanner.rejected.message', {
          defaultValue: 'Payment gateway setup was rejected. Contact support for assistance.',
        }),
      },
      {
        status: 'requires_action',
        severity: 'error',
        message: t('paymentGatewayBanner.requiresAction.message', {
          defaultValue: 'Payment gateway requires action to continue accepting payments.',
        }),
        actionLabel: t('paymentGatewayBanner.requiresAction.action', {
          defaultValue: 'Complete Now',
        }),
        actionType: 'onboarding',
      },
      {
        status: 'in_progress',
        severity: 'warning',
        message: t('paymentGatewayBanner.inProgress.message', {
          defaultValue:
            'Payment gateway setup is in progress. Complete onboarding to accept payments.',
        }),
        actionLabel: t('paymentGatewayBanner.inProgress.action', {
          defaultValue: 'Continue Setup',
        }),
        actionType: 'onboarding',
      },
      {
        status: 'pending_verification',
        severity: 'info',
        message: t('paymentGatewayBanner.pendingVerification.message', {
          defaultValue:
            "Payment gateway is being verified by Stripe. We'll notify you once complete.",
        }),
      },
    ]

    return BANNER_CONFIG.find((c) => c.status === onboardingStatus) || null
  }, [onboardingStatus, hasEventuallyDueRequirements, t])

  if (!bannerInfo) {
    return null
  }

  const colors = SEVERITY_COLORS[bannerInfo.severity] || SEVERITY_COLORS.warning
  const bannerStyles = getBannerStyles(colors)

  return (
    <Box sx={bannerStyles.root}>
      <Typography sx={bannerStyles.message}>{bannerInfo.message}</Typography>
      {bannerInfo.actionLabel && (
        <Typography
          component="button"
          onClick={() => handleAction(bannerInfo.actionType)}
          sx={{
            ...bannerStyles.actionButton,
            cursor: isLoading ? 'wait' : 'pointer',
          }}
        >
          {isLoading ? (
            <CircularProgress size={12} sx={bannerStyles.spinner} />
          ) : (
            bannerInfo.actionLabel
          )}
        </Typography>
      )}
    </Box>
  )
}

export default PaymentGatewayBanner

PaymentGatewayBanner.propTypes = {
  siteId: PropTypes.string,
  onboardingStatus: PropTypes.string,
  hasEventuallyDueRequirements: PropTypes.bool,
}
